import { Dispatch } from 'react-redux';

import { StoreState } from '../../../reducers';
import { Action } from '../../../actions';
import { isEditorOpened } from './utils';
import { setVersion, setIsEditorLoaded, reset } from './ui';
import { getData } from './sources';
import { changePageSpread } from './pagesController';
import * as helpers from '../utils/helpers';
import { createNotification } from '../../NotificationBar/actions';
import ApiUtils from '../../../utils/api';

export enum PagesSelectionActionKeys {
	SET_PAGES_SELECTION = '@@editor/pagesSelection/SET_PAGES_SELECTION',
	RESET_PAGES_SELECTION = '@@editor/pagesSelection/RESET_PAGES_SELECTION',
	SELECT_PAGE_SPREAD = '@@editor/pagesSelection/SELECT_PAGE_SPREAD',
	RESTORE_PAGES_SELECTION = '@@editor/pagesSelection/RESTORE_PAGES_SELECTION',
}

interface SetPagesSelectionAction extends Action {
	type: PagesSelectionActionKeys.SET_PAGES_SELECTION;
	currentSpreadIndex: number;
}

interface ResetPagesSelectionAction extends Action {
	type: PagesSelectionActionKeys.RESET_PAGES_SELECTION;
}

interface SelectPageSpreadAction extends Action {
	type: PagesSelectionActionKeys.SELECT_PAGE_SPREAD;
	spreadIndex: number;
	allSpreads: number[][];
	isCtrlPressed?: boolean;
	isShiftPressed?: boolean;
}

interface RestorePagesSelectionAction extends Action {
	type: PagesSelectionActionKeys.RESTORE_PAGES_SELECTION;
	spreadIndices: number[];
}

export type PagesSelectionAction =
	SetPagesSelectionAction |
	ResetPagesSelectionAction |
	SelectPageSpreadAction |
	RestorePagesSelectionAction;

export const setPagesSelection = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const {
		editor: {
			pagesController: { currentSpreadIndex },
		}
	} = getState();

	const action: SetPagesSelectionAction = {
		type: PagesSelectionActionKeys.SET_PAGES_SELECTION,
		currentSpreadIndex,
	};

	dispatch(action);
};

export const resetPagesSelection = (): ResetPagesSelectionAction => ({
	type: PagesSelectionActionKeys.RESET_PAGES_SELECTION,
});

export const selectPageSpread = (spreadIndex: number, isCtrlPressed?: boolean, isShiftPressed?: boolean) => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void => {
	const {
		editor: {
			pagesController: { spreads },
		}
	} = getState();

	const action: SelectPageSpreadAction = {
		type: PagesSelectionActionKeys.SELECT_PAGE_SPREAD,
		spreadIndex,
		allSpreads: spreads,
		isCtrlPressed,
		isShiftPressed,
	};

	dispatch(action);
};

const restorePagesSelection = (spreadIndices: number[]): RestorePagesSelectionAction => ({
	type: PagesSelectionActionKeys.RESTORE_PAGES_SELECTION,
	spreadIndices,
});

export const removePages = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		dispatch(setIsEditorLoaded(false));

		const {
			editor: {
				pagesController: { currentSpreadIndex, spreads },
				pagesSelection: { spreadIndices, isActive },
			},
		} = getState();

		const { clientId, tempGId: resourceId } = helpers.getClientAndResourceIds();
		const spreadsToRemove = isActive ? spreadIndices : spreads[currentSpreadIndex];
		const firstSpreadIndexToRemove = isActive ? spreadIndices[0] : currentSpreadIndex;
		const spreadIndexTogo = firstSpreadIndexToRemove >= (spreads.length - spreadsToRemove.length)
			? firstSpreadIndexToRemove - 1
			: firstSpreadIndexToRemove;
		const pagesToRemove = isActive
			? helpers.getSelectedPages(spreadIndices, spreads)
			: spreads[currentSpreadIndex];

		await ApiUtils.removePublicationPages(clientId, resourceId, pagesToRemove);
		await dispatch(finishPagesUpdate([spreadIndexTogo], isActive));
	} catch (error) {
		dispatch(setIsEditorLoaded(true));
		dispatch(createNotification('error.remove_page_error'));
	}
};

export const movePages = (spreadIndices: number[], newSpreadPosition: number) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		dispatch(setIsEditorLoaded(false));

		const {
			editor: {
				pagesController: { spreads },
				pagesSelection,
			},
		} = getState();

		const { clientId, tempGId: resourceId } = helpers.getClientAndResourceIds();
		const isMoveToTop = spreadIndices[0] > newSpreadPosition;
		const pages = helpers.getSelectedPages(spreadIndices, spreads);
		const newSpreadIndices = spreadIndices.map((spreadIndex, index) => (
			isMoveToTop
				? newSpreadPosition + index
				: newSpreadPosition + index - (spreadIndices.length - 1)
		));
		const destinationSpread = spreads[newSpreadPosition];
		const newPagePosition = destinationSpread[isMoveToTop ? 0 : destinationSpread.length - 1];

		await ApiUtils.movePublicationPages(clientId, resourceId, pages, newPagePosition);
		await dispatch(finishPagesUpdate(newSpreadIndices, pagesSelection.isActive));
	} catch (error) {
		dispatch(setIsEditorLoaded(true));
		dispatch(createNotification('error.move_page_error'));
	}
};

export const duplicatePages = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		dispatch(setIsEditorLoaded(false));

		const {
			editor: {
				pagesController: { currentSpreadIndex, spreads, displayMode },
				pagesSelection: { spreadIndices, isActive },
				configs: { pagesData },
			},
		} = getState();

		const { clientId, tempGId: resourceId } = helpers.getClientAndResourceIds();
		const spreadIndexToDuplicate = isActive ? spreadIndices[0] : currentSpreadIndex;
		const newSpreadIndex = helpers.getActiveSpreadIndexAfterPagesUpdate(spreadIndexToDuplicate, spreads, displayMode, pagesData);
		const pagesToDuplicate = isActive && spreadIndices.length === 1
			? helpers.getSelectedPages(spreadIndices, spreads)
			: spreads[currentSpreadIndex];

		await ApiUtils.duplicatePublicationPages(clientId, resourceId, pagesToDuplicate);
		await dispatch(finishPagesUpdate([newSpreadIndex]));
	} catch (error) {
		dispatch(setIsEditorLoaded(true));
		dispatch(createNotification('error.duplicate_page_error'));
	}
};

const finishPagesUpdate = (newSpreadIndices: number[], isPagesSelectionActive?: boolean) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		dispatch(setVersion(Date.now()));
		dispatch(reset(true));

		if (isEditorOpened()) {
			const { clientId, tempGId: resourceId } = helpers.getClientAndResourceIds();
			await dispatch(getData(clientId, resourceId, false));

			const {
				editor: {
					pagesController: { totalPages },
				},
			} = getState();

			if (isPagesSelectionActive && totalPages > 1) {
				dispatch(restorePagesSelection(newSpreadIndices));
			}

			dispatch(changePageSpread(newSpreadIndices[0], false));
		}
	} catch (error) {
		dispatch(createNotification('error.fetch_editor_data_error'));
	}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/actions/pagesSelection.ts